import { TrainFront } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { mapsEmbed } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SectionHead, ContactLines, DirectionsButton, useContact } from "@/components/site/Contact";

export function Location() {
  const { t } = useLang();
  const c = useContact();
  return (
    <section id="location" className="border-y border-white/5 bg-[#0c0d11]" data-testid="location-section">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-10 lg:py-28">
        <div className="lg:col-span-4">
          <SectionHead eyebrow={t("loc.eyebrow")} title={t("loc.title")} />
          <ContactLines id="location" />
          <p className="mt-6 flex items-center gap-2 text-sm text-slate-400"><TrainFront size={16} className="text-red-500" /> {t("loc.metro")}</p>
          <div className="mt-8"><DirectionsButton id="location" className="btn-red border-0" /></div>
        </div>
        <div className="lg:col-span-8">
          <div className="brackets relative overflow-hidden rounded-2xl border border-white/10 bg-[#111318]" style={{ minHeight: 360 }}>
            <p className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-slate-500">{t("loc.mapOff")}</p>
            <iframe title="RECORDING ROOM map" data-testid="location-map" src={mapsEmbed(c.map_query || c.address)} loading="lazy"
              referrerPolicy="no-referrer-when-downgrade" className="relative h-[360px] w-full border-0 sm:h-[460px]"
              style={{ filter: "grayscale(0.9) invert(0.92) contrast(0.9)" }} />
          </div>
        </div>
      </div>
    </section>
  );
}

export function Faq() {
  const { t } = useLang();
  const { site } = useSite();
  const rollover = site?.booking?.rollover;
  const items = [1, 2, 3, 4, 5, 6].map((n) => [t(`faq.q${n}`), t(`faq.a${n}`)]);
  items.push([t("faq.q7"), rollover ? t("faq.a7r") : t("faq.a7e")]);
  return (
    <section id="faq" className="mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:px-10 lg:py-28" data-testid="faq-section">
      <SectionHead eyebrow={t("faq.eyebrow")} title={t("faq.title")} />
      <Accordion type="single" collapsible className="panel px-5 sm:px-8">
        {items.map(([q, a], i) => (
          <AccordionItem key={i} value={`q${i}`} className="border-white/10">
            <AccordionTrigger data-testid={`faq-item-${i}`} className="text-left text-base text-white hover:no-underline">{q}</AccordionTrigger>
            <AccordionContent className="text-slate-400">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

export function ContactSection() {
  const { t } = useLang();
  return (
    <section id="contact" className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-10" data-testid="contact-section">
      <div className="panel brackets grid gap-10 p-6 sm:p-10 md:grid-cols-2">
        <SectionHead eyebrow={t("contact.eyebrow")} title={t("contact.title")} />
        <div className="flex flex-col gap-8">
          <ContactLines id="contact" />
          <DirectionsButton id="contact" className="self-start" />
        </div>
      </div>
    </section>
  );
}
